import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

import type {
  GetLeadCountByStampIdRequest,
  GetLeadDetailsRequest,
  LeadCreateResponse,
  ListLeadsRequest,
  SaveUserSelectionResponse,
} from "@pbz/schema/lead";

import { QUERY_KEYS } from "@/utils/constants";
import { orpc } from "@/utils/orpc-client";

export function useCreateLead({
  onSuccess,
  onError,
}: {
  onSuccess: (data: LeadCreateResponse) => void;
  onError: (error: Error) => void;
}) {
  return useMutation(
    orpc.lead.create.mutationOptions({
      onSuccess: (response) => onSuccess(response.data),
      onError,
    }),
  );
}

export function useUpdateLeadDetails({
  onSuccess,
  onError,
}: {
  onSuccess: (data: { message: string }) => void;
  onError: (error: Error) => void;
}) {
  return useMutation(
    orpc.lead.update.mutationOptions({
      onSuccess: (response) => onSuccess(response.data),
      onError,
    }),
  );
}

export function useGetQuotes(leadId: number) {
  return useQuery(
    orpc.lead.quotes.queryOptions({
      input: { leadId },
      queryKey: [QUERY_KEYS.GET_QUOTES, leadId],
    }),
  );
}

export function useSaveUserSelection({
  onSuccess,
  onError,
}: {
  onSuccess: (data: SaveUserSelectionResponse) => void;
  onError: (error: Error) => void;
}) {
  return useMutation(
    orpc.lead.saveSelection.mutationOptions({
      onSuccess: (response) => onSuccess(response.data),
      onError,
    }),
  );
}

export function useListLeads(params: ListLeadsRequest) {
  return useQuery(
    orpc.lead.list.queryOptions({
      input: params,
      queryKey: [QUERY_KEYS.LEAD_LIST, params],
      placeholderData: keepPreviousData,
    }),
  );
}

export function useGetLead(params: GetLeadDetailsRequest, enabled: boolean) {
  return useQuery(
    orpc.lead.detail.queryOptions({
      input: params,
      queryKey: [QUERY_KEYS.LEAD_DETAIL, params],
      enabled,
    }),
  );
}

export function useGetLeadTabCount(params: GetLeadCountByStampIdRequest) {
  return useQuery(
    orpc.lead.count.queryOptions({
      input: params,
      queryKey: [QUERY_KEYS.LEAD_DETAIL, params],
      placeholderData: keepPreviousData,
    }),
  );
}

export function useGetLeadView(
  params: GetLeadDetailsRequest,
  enabled: boolean,
) {
  return useQuery(
    orpc.lead.view.queryOptions({
      input: params,
      queryKey: [QUERY_KEYS.LEAD_VIEW, params],
      enabled,
    }),
  );
}
